/**
 * Multi-select combobox built on `@radix-ui/react-popover`.
 *
 * Same shape and keyboard story as `<Combobox>`, but values is an array
 * with a checkbox in each row plus "Select all visible" / "Clear" rail
 * actions. We keep the list shallow (no virtualisation) since the
 * filter rails it powers top out at a few hundred entries; if we ever
 * need to reach for tens of thousands this is the place to swap in
 * `react-virtual`.
 *
 * Keyboard support:
 *   - ↑/↓ moves the cursor
 *   - Enter toggles the active row (popover stays open); Space does the
 *     same, but only while the search box is empty

 *   - ⌘/⌃+A selects all currently visible (after search filter)
 *   - Esc closes
 */
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

export interface MultiComboboxOption {
  value: string;
  label: string;
  hint?: string;
}

export interface MultiComboboxProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  width?: number;
  triggerClassName?: string;
  /** Label shown when nothing is selected (e.g. "All brands"). */
  allLabel?: string;
  /**
   * If provided, the trigger pill shows `${count} <noun>` instead of the
   * selected option labels — useful for filter rails that get crowded
   * fast (vendors, categories…).
   */
  countNoun?: string;
  /**
   * Mirror of the internal search box. Supply this when the option list is
   * fetched from the server, so one field both filters and searches — two
   * separate boxes for one field is a trap.
   */
  onSearchChange?: (query: string) => void;
}

export function MultiCombobox({
  values,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  disabled,
  loading,
  width,
  triggerClassName,
  allLabel,
  countNoun,
  onSearchChange,
}: MultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const valueSet = useMemo(() => new Set(values), [values]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.hint?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, query]);

  // Reset search + cursor each time we re-open. We keep the existing
  // selection intact — closing/reopening shouldn't drop the user's work.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    onSearchChange?.('');
    setActiveIdx(0);
    requestAnimationFrame(() => inputRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (activeIdx >= filtered.length) {
      setActiveIdx(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, activeIdx]);

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${activeIdx}"]`,
    );
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  function toggle(value: string) {
    if (valueSet.has(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  }

  function selectAllVisible() {
    const next = new Set(values);
    for (const o of filtered) next.add(o.value);
    onChange(Array.from(next));
  }

  function clearAll() {
    onChange([]);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' || (e.key === ' ' && query === '')) {
      // Space only toggles while the search box is empty — otherwise it
      // would be impossible to type a multi-word option (e.g. a full name).
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) toggle(opt.value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
      // Pre-empt the browser's text-select; a filter rail almost never
      // needs to highlight every char in the search box.
      e.preventDefault();
      selectAllVisible();
    }
  }

  const triggerLabel = useMemo(() => {
    if (loading) return 'Loading…';
    if (values.length === 0) return allLabel ?? placeholder;
    if (countNoun) {
      return `${values.length} ${countNoun}${values.length === 1 ? '' : 's'}`;
    }
    if (values.length <= 2) {
      return values
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ');
    }
    const first = options.find((o) => o.value === values[0])?.label ?? values[0];
    return `${first} +${values.length - 1}`;
  }, [values, options, loading, placeholder, allLabel, countNoun]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          style={width ? { width } : undefined}
          className={cn(
            'press inline-flex h-9 items-center justify-between gap-2 rounded-md border border-input bg-surface px-3 text-xs focus-ring',
            'disabled:cursor-not-allowed disabled:opacity-60',
            triggerClassName,
          )}
        >
          <span
            className={cn(
              'truncate text-left',
              values.length === 0 && 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </span>
          <span className="inline-flex items-center gap-1 shrink-0">
            {values.length > 0 ? (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  clearAll();
                }}
                className="press rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
                aria-label="Clear selection"
              >
                <X className="h-3 w-3" aria-hidden />
              </span>
            ) : null}
            <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
          </span>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'select-anim z-[1100] overflow-hidden rounded-md border border-border bg-surface-elevated text-foreground shadow-soft',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-150 ease-out',
          )}
          style={{ width: width ?? 260 }}
        >
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <Search
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-hidden
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                onSearchChange?.(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="h-7 w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
              aria-controls={listboxId}
              aria-autocomplete="list"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  onSearchChange?.('');
                  inputRef.current?.focus();
                }}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-2 py-1 text-[10px] text-muted-foreground">
            <span>
              {values.length > 0 ? (
                <>
                  <span className="font-medium text-foreground">
                    {values.length}
                  </span>{' '}
                  selected
                </>
              ) : (
                'None selected'
              )}
            </span>
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                onClick={selectAllVisible}
                disabled={filtered.length === 0}
                className="press rounded-sm px-1.5 py-0.5 hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                Select all visible
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={values.length === 0}
                className="press rounded-sm px-1.5 py-0.5 hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                Clear
              </button>
            </span>
          </div>

          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-multiselectable
            className="max-h-64 overflow-y-auto p-1"
          >
            {filtered.length === 0 ? (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              filtered.map((opt, i) => {
                const isSel = valueSet.has(opt.value);
                const isActive = i === activeIdx;
                return (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={isSel}
                    data-idx={i}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs',
                      isActive && 'bg-muted',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
                        isSel
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-surface',
                      )}
                    >
                      {isSel ? (
                        <Check className="h-2.5 w-2.5" aria-hidden />
                      ) : null}
                    </span>
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint ? (
                      <span className="text-[10px] text-muted-foreground">
                        {opt.hint}
                      </span>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-1 text-[10px] text-muted-foreground">
            <span>
              Showing{' '}
              <span className="font-mono text-foreground">
                {filtered.length}
              </span>
              /{options.length}
            </span>
            <span className="font-mono">↑↓ · Enter · Esc</span>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
